import React, { useEffect, useState } from 'react';
import { Table, Pagination, Badge, Spinner, Modal, Button } from 'react-bootstrap';
import api from '../../api';
import styles from './styles.module.css';

interface IntegracaoSigaLog {
  id: string;
  bancaId: string;
  processoIdentificador: string;
  tentativa: number;
  status: 'PENDENTE' | 'SUCESSO' | 'FALHA' | 'FALHA_DEFINITIVA';
  erroMensagem: string | null;
  createdAt: string;
  requestPayload?: string;
  responsePayload?: string;
}

interface PageResponse {
  content: IntegracaoSigaLog[];
  totalPages: number;
  totalElements: number;
  number: number;
  size: number;
}

const IntegracaoSigaLogs: React.FC = () => {
  const [logs, setLogs] = useState<IntegracaoSigaLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [selectedLog, setSelectedLog] = useState<IntegracaoSigaLog | null>(null);
  const [showModal, setShowModal] = useState(false);

  const fetchLogs = async (page: number = 0) => {
    try {
      setLoading(true);
      const response = await api.get<PageResponse>('/integracao-siga/logs', {
        params: {
          page,
          size: 20,
          sort: 'createdAt,desc'
        }
      });
      setLogs(response.data.content);
      setTotalPages(response.data.totalPages);
      setTotalElements(response.data.totalElements);
      setCurrentPage(response.data.number);
    } catch (error) {
      console.error('Erro ao buscar logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const handlePageChange = (page: number) => {
    fetchLogs(page);
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, string> = {
      SUCESSO: 'success',
      FALHA: 'warning',
      FALHA_DEFINITIVA: 'danger',
      PENDENTE: 'secondary'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status.replace('_', ' ')}</Badge>;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const handleRowClick = (log: IntegracaoSigaLog) => {
    setSelectedLog(log);
    setShowModal(true);
  };

  const formatJSON = (jsonString: string | undefined) => {
    if (!jsonString) return 'N/A';
    try {
      return JSON.stringify(JSON.parse(jsonString), null, 2);
    } catch {
      return jsonString;
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Logs de Integração SIGA</h1>
        <p className={styles.subtitle}>Total de registros: {totalElements}</p>
      </div>

      {loading ? (
        <div className={styles.loading}>
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          <div className={styles.tableContainer}>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Data/Hora</th>
                  <th>Processo</th>
                  <th>Banca ID</th>
                  <th>Tentativa</th>
                  <th>Status</th>
                  <th>Erro</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id} onClick={() => handleRowClick(log)}>
                    <td className={styles.bancaId}>{log.id.substring(0, 8)}...</td>
                    <td>{formatDate(log.createdAt)}</td>
                    <td>{log.processoIdentificador}</td>
                    <td className={styles.bancaId}>{log.bancaId}</td>
                    <td className={styles.tentativa}>{log.tentativa}</td>
                    <td>{getStatusBadge(log.status)}</td>
                    <td className={styles.erro}>
                      {log.erroMensagem ? (
                        <span title={log.erroMensagem}>
                          {log.erroMensagem.substring(0, 40)}
                          {log.erroMensagem.length > 40 && '...'}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className={styles.paginationContainer}>
              <Pagination>
                <Pagination.First onClick={() => handlePageChange(0)} disabled={currentPage === 0} />
                <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 0} />
                
                {[...Array(totalPages)].map((_, index) => {
                  if (
                    index === 0 ||
                    index === totalPages - 1 ||
                    (index >= currentPage - 2 && index <= currentPage + 2)
                  ) {
                    return (
                      <Pagination.Item
                        key={index}
                        active={index === currentPage}
                        onClick={() => handlePageChange(index)}
                      >
                        {index + 1}
                      </Pagination.Item>
                    );
                  } else if (index === currentPage - 3 || index === currentPage + 3) {
                    return <Pagination.Ellipsis key={index} disabled />;
                  }
                  return null;
                })}

                <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages - 1} />
                <Pagination.Last onClick={() => handlePageChange(totalPages - 1)} disabled={currentPage === totalPages - 1} />
              </Pagination>
            </div>
          )}
        </>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title>Detalhes do Log</Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          {selectedLog && (
            <div>
              <div className={styles.detailSection}>
                <h6>Informações Gerais</h6>
                <div className={styles.detailGrid}>
                  <div><strong>ID:</strong> {selectedLog.id}</div>
                  <div><strong>Banca ID:</strong> {selectedLog.bancaId}</div>
                  <div><strong>Processo:</strong> {selectedLog.processoIdentificador}</div>
                  <div><strong>Tentativa:</strong> {selectedLog.tentativa}</div>
                  <div><strong>Status:</strong> {getStatusBadge(selectedLog.status)}</div>
                  <div><strong>Data/Hora:</strong> {formatDate(selectedLog.createdAt)}</div>
                </div>
              </div>

              {selectedLog.erroMensagem && (
                <div className={styles.detailSection}>
                  <h6>Mensagem de Erro</h6>
                  <pre className={styles.errorPre}>{selectedLog.erroMensagem}</pre>
                </div>
              )}

              {selectedLog.requestPayload && (
                <div className={styles.detailSection}>
                  <h6>Request Payload</h6>
                  <pre className={styles.jsonPre}>{formatJSON(selectedLog.requestPayload)}</pre>
                </div>
              )}

              {selectedLog.responsePayload && (
                <div className={styles.detailSection}>
                  <h6>Response Payload</h6>
                  <pre className={styles.jsonPre}>{formatJSON(selectedLog.responsePayload)}</pre>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default IntegracaoSigaLogs;
